// App.js - Updated routing with new granular permissions
/* eslint-disable react/prop-types */
import {
  Route,
  BrowserRouter as Router,
  Switch,
  Redirect,
} from "react-router-dom";

import Login from "./authentication/Login";
import Register from "./authentication/Register";

import Dashboard from "./boat-cruise/dashboard/Dashboard";

import {
  AddDeck,
  DecksList,
  SchedulesList,
} from "./boat-cruise/boat/BoatPages";
import PaymentsPage from "./boat-cruise/payments/PaymentsPage";
import EditDeck from "./boat-cruise/boat/EditDeck";

import TripsList from "./boat-cruise/trips/TripsList";
import AddTrip from "./boat-cruise/trips/AddTrip";
import EditTrip from "./boat-cruise/trips/EditTrip";
import TripDetail from "./boat-cruise/trips/TripDetail";

import PricingPage from "./boat-cruise/pricing/PricingPage";

import ReservationsList from "./boat-cruise/reservations/ReservationsList";
import AddReservation from "./boat-cruise/reservations/AddReservation";
import ReservationDetail from "./boat-cruise/reservations/ReservationDetail";
import MyReservations from "./boat-cruise/reservations/Myreservations";

import { TicketsList, ScanQR } from "./boat-cruise/tickets/TicketsPages";
import MyTickets from "./boat-cruise/tickets/Mytickets";

import ReportsPage from "./boat-cruise/reports/ReportsPage";

import AdminUsers from "./boat-cruise/users/AdminUsers";
import CustomerUsers from "./boat-cruise/users/CustomerUsers";
import AddAdmin from "./boat-cruise/users/addAdmin";
import AddCustomer from "./boat-cruise/users/AddCustomer";
import EditAdmin from "./boat-cruise/users/EditAdmin";
import EditCustomer from "./boat-cruise/users/EditCustomer";
import Profile from "./boat-cruise/users/Profile";
import RolesList from "./boat-cruise/roles/RolesList";
import AddRole from "./boat-cruise/roles/AddRole";
import EditRole from "./boat-cruise/roles/EditRole";

import Page403 from "./pages/errorpages/403";
import Page404 from "./pages/errorpages/404";

import PromoCodesList from "./boat-cruise/PromoCodes/PromoCodesList";
import GeneratePromoCodes from "./boat-cruise/PromoCodes/GeneratePromoCodes";
import PromoCodeDetails from "./boat-cruise/PromoCodes/PromoCodeDetails.jsx";
import RefundsList from "./boat-cruise/refunds/RefundsList";
import AuditLogPage from "./boat-cruise/audit/AuditLogPage";
import RequestsList from "./boat-cruise/requests/RequestsList";

const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem("user");
    if (!userStr) return null;

    const user = JSON.parse(userStr);
    if (!user.permissions && user.role && user.role.permissions) {
      user.permissions = user.role.permissions;
    }
    return user;
  } catch {
    return null;
  }
};

const isAdminUser = (user) => {
  if (!user) return false;
  const raw = user.role;
  if (!raw) return false;
  const name = (typeof raw === "string" ? raw : raw?.name || "").toLowerCase();
  return name === "admin";
};

const hasPermission = (user, permissionName) => {
  if (!user) return false;

  const permissions = user.permissions;
  if (!permissions || !Array.isArray(permissions)) return false;

  return permissions.some((p) => {
    if (typeof p === "string") {
      return p === permissionName;
    }
    return p.name === permissionName;
  });
};

const hasAnyPermission = (user, permissions) => {
  if (!user) return false;
  return permissions.some((permission) => hasPermission(user, permission));
};

const getHomePath = () => {
  const user = getStoredUser();
  if (!user) return "/login";

  if (isAdminUser(user)) return "/dashboard";
  return "/reservations/my";
};

const Footer = () => (
  <footer
    style={{
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: "#f8f9fa",
      padding: "10px 0",
      textAlign: "center",
      borderTop: "1px solid #dee2e6",
      zIndex: 1000,
    }}
  >
    <div className="container">
      <p className="mb-0" style={{ color: "#6c757d", fontSize: "14px" }}>
        © 2026 Boat Cruise. All rights reserved.
      </p>
    </div>
  </footer>
);

const LayoutWithFooter = ({ children }) => (
  <div style={{ paddingBottom: "50px" }}>
    {children}
    <Footer />
  </div>
);

const PrivateRoute = ({ component: Component, ...rest }) => {
  const token = localStorage.getItem("access_token");
  return (
    <Route
      {...rest}
      render={(props) =>
        token ? (
          <LayoutWithFooter>
            <Component {...props} />
          </LayoutWithFooter>
        ) : (
          <Redirect
            to={{ pathname: "/login", state: { from: props.location } }}
          />
        )
      }
    />
  );
};

const PermissionRoute = ({
  component: Component,
  permissions,
  requireAll = false,
  fallbackPath = "/403",
  ...rest
}) => {
  const token = localStorage.getItem("access_token");
  const user = getStoredUser();

  const hasRequiredPermissions = () => {
    if (!user) return false;

    if (!permissions || permissions.length === 0) {
      return true;
    }

    if (requireAll) {
      return permissions.every((permission) => hasPermission(user, permission));
    } else {
      return hasAnyPermission(user, permissions);
    }
  };

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!token) {
          return (
            <Redirect
              to={{ pathname: "/login", state: { from: props.location } }}
            />
          );
        }

        if (!hasRequiredPermissions()) {
          return <Redirect to={fallbackPath} />;
        }

        return (
          <LayoutWithFooter>
            <Component {...props} />
          </LayoutWithFooter>
        );
      }}
    />
  );
};

const AppContainer = () => (
  <Router basename="/">
    <Switch>
      {/* Public Routes */}
      <Route path="/login" exact component={Login} />
      <Route path="/admin-login" exact component={Login} />
      <Route path="/register" exact component={Register} />

      {/* Home Redirect */}
      <Route exact path="/" render={() => <Redirect to={getHomePath()} />} />

      {/* Dashboard - Reports view permission */}
      <PermissionRoute
        path="/dashboard"
        exact
        component={Dashboard}
        permissions={["reports.view"]}
      />

      {/* Boat & Decks Routes - Granular permissions */}
      <PermissionRoute
        path="/boat/decks"
        exact
        component={DecksList}
        permissions={["boat.view"]}
      />
      <PermissionRoute
        path="/boat/decks/add"
        exact
        component={AddDeck}
        permissions={["boat.create"]}
      />
      <PermissionRoute
        path="/boat/decks/:id/edit"
        exact
        component={EditDeck}
        permissions={["boat.edit"]}
      />

      {/* Schedules Routes */}
      <PermissionRoute
        path="/schedules"
        exact
        component={SchedulesList}
        permissions={["schedules.view"]}
      />

      {/* Trips Routes - Granular permissions */}
      <PermissionRoute
        path="/trips"
        exact
        component={TripsList}
        permissions={["trips.view"]}
      />
      <PermissionRoute
        path="/trips/add"
        exact
        component={AddTrip}
        permissions={["trips.create"]}
      />
      <PermissionRoute
        path="/trips/:id/edit"
        exact
        component={EditTrip}
        permissions={["trips.edit"]}
      />
      <PrivateRoute path="/trips/:id" exact component={TripDetail} />

      {/* Pricing - View and manage permissions */}
      <PermissionRoute
        path="/pricing"
        exact
        component={PricingPage}
        permissions={["pricing.view"]}
      />

      {/* Reservations Routes - Granular permissions */}
      <PermissionRoute
        path="/reservations/add"
        exact
        component={AddReservation}
        permissions={["reservations.create"]}
      />
      <PrivateRoute path="/reservations/my" exact component={MyReservations} />
      <PermissionRoute
        path="/reservations"
        exact
        component={ReservationsList}
        permissions={["reservations.view"]}
      />
      <PermissionRoute
        path="/reservations/:id"
        exact
        component={ReservationDetail}
        permissions={["reservations.view"]}
      />

      {/* Payments Routes */}
      <PermissionRoute
        path="/payments"
        exact
        component={PaymentsPage}
        permissions={["payments.view"]}
      />

      {/* Tickets Routes */}
      <PermissionRoute
        path="/tickets/scan"
        exact
        component={ScanQR}
        permissions={["tickets.check_in"]}
      />
      <PrivateRoute path="/tickets/my" exact component={MyTickets} />
      <PermissionRoute
        path="/tickets"
        exact
        component={TicketsList}
        permissions={["tickets.view"]}
      />

      {/* Reports Routes */}
      <PermissionRoute
        path="/reports"
        exact
        component={ReportsPage}
        permissions={["reports.view"]}
      />
      <PermissionRoute
        path="/reports/revenue"
        exact
        component={ReportsPage}
        permissions={["reports.view"]}
      />
      <PermissionRoute
        path="/reports/utilization"
        exact
        component={ReportsPage}
        permissions={["reports.view"]}
      />
      <PermissionRoute
        path="/reports/customers"
        exact
        component={ReportsPage}
        permissions={["reports.view"]}
      />

      {/* User Management Routes - Granular permissions */}
      <PrivateRoute path="/profile" exact component={Profile} />
      <PermissionRoute
        path="/audit-logs"
        exact
        component={AuditLogPage}
        permissions={["audit.view"]}
      />
      <PermissionRoute
        path="/requests"
        exact
        component={RequestsList}
        permissions={["requests.manage"]}
      />
      <PermissionRoute
        path="/users/admins"
        exact
        component={AdminUsers}
        permissions={["users.view"]}
      />
      <PermissionRoute
        path="/users/customers"
        exact
        component={CustomerUsers}
        permissions={["users.view"]}
      />
      <PermissionRoute
        path="/users/add-admin"
        exact
        component={AddAdmin}
        permissions={["users.create"]}
      />
      <PermissionRoute
        path="/users/add-customer"
        exact
        component={AddCustomer}
        permissions={["users.create"]}
      />
      <PermissionRoute
        path="/users/edit-admin/:id"
        exact
        component={EditAdmin}
        permissions={["users.edit"]}
      />
      <PermissionRoute
        path="/users/edit-customer/:id"
        exact
        component={EditCustomer}
        permissions={["users.edit"]}
      />

      {/* Roles & Permissions Routes - Granular permissions */}
      <PermissionRoute
        path="/roles"
        exact
        component={RolesList}
        permissions={["roles.view"]}
      />
      <PermissionRoute
        path="/roles/add"
        exact
        component={AddRole}
        permissions={["roles.create"]}
      />
      <PermissionRoute
        path="/roles/:id/edit"
        exact
        component={EditRole}
        permissions={["roles.edit"]}
      />
      <PermissionRoute
        path="/promo-codes"
        exact
        component={PromoCodesList}
        permissions={["promocodes.view"]}
      />
      <PermissionRoute
        path="/promo-codes/generate"
        exact
        component={GeneratePromoCodes}
        permissions={["promocodes.create"]}
      />
      <PermissionRoute
        path="/promo-codes/:id"
        exact
        component={PromoCodeDetails}
        permissions={["promocodes.view"]}
      />

      <PermissionRoute
        path="/refunds"
        exact
        component={RefundsList}
        permissions={["reservations.refund"]}
      />
      {/* Error Pages */}
      <Route path="/403" exact component={Page403} />
      <Route component={Page404} />
    </Switch>
  </Router>
);

export default AppContainer;
